import { AuthProvider, ReauthError, ReauthErrorKind } from '@griply/types';
import { getReauthErrorMessageKey } from '@griply/utils';
import { reauthenticate } from 'features/user';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getErrorReporter } from 'shared/services/error-tracking';

const REAUTHABLE_PROVIDERS = [
  AuthProvider.Email,
  AuthProvider.Google,
  AuthProvider.Apple,
];

const {
  getProvider,
  isLoginRecent,
  withPassword,
  withGoogleRedirect,
  withAppleRedirect,
} = reauthenticate;

export const useDeleteAccountReauth = () => {
  const { t } = useTranslation();

  const [provider, setProvider] = useState<AuthProvider | null>(null);
  const [password, setPassword] = useState('');
  const [errorKind, setErrorKind] = useState<ReauthErrorKind | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const errorMessage = useMemo(() => {
    if (!errorKind) {
      return undefined;
    }
    return t(getReauthErrorMessageKey(errorKind));
  }, [errorKind, t]);

  // Resolves the signed-in provider and reports whether a reauth step is needed
  // before deletion. A login inside the recent window — notably right after the
  // Google/Apple redirect returns — skips reauth; without that the social path
  // would bounce the user back into the redirect forever.
  const prepare = useCallback(async (): Promise<boolean> => {
    const resolved = await getProvider();
    setProvider(resolved);
    if (!resolved || !REAUTHABLE_PROVIDERS.includes(resolved)) {
      return false;
    }
    const recent = await isLoginRecent(new Date());
    return !recent;
  }, []);

  const submitPassword = useCallback(async (): Promise<boolean> => {
    setIsSubmitting(true);
    setErrorKind(null);
    try {
      await withPassword(password);
      return true;
    } catch (error: unknown) {
      setErrorKind(
        error instanceof ReauthError ? error.kind : ReauthErrorKind.Unknown,
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [password]);

  const startSocialRedirect = useCallback(() => {
    const onRedirectError = (error: unknown) => {
      getErrorReporter().captureException(error, {
        tags: { source: 'delete-account-reauth-redirect' },
      });
      setErrorKind(ReauthErrorKind.Unknown);
    };
    if (provider === AuthProvider.Google) {
      withGoogleRedirect().catch(onRedirectError);
      return;
    }
    if (provider === AuthProvider.Apple) {
      withAppleRedirect().catch(onRedirectError);
    }
  }, [provider]);

  const onChangePassword = useCallback((value: string) => {
    setPassword(value);
    setErrorKind(null);
  }, []);

  const reset = useCallback(() => {
    setProvider(null);
    setPassword('');
    setErrorKind(null);
    setIsSubmitting(false);
  }, []);

  return {
    provider,
    password,
    onChangePassword,
    errorMessage,
    isSubmitting,
    prepare,
    submitPassword,
    startSocialRedirect,
    reset,
  };
};
